import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MdPaid } from 'react-icons/md';
import { useForm } from 'react-hook-form';
import {
  FaCheckCircle,
  FaCopy,
  FaFilePdf,
  FaRegEdit,
  FaSignature,
  FaTimes,
} from 'react-icons/fa';
import InvoiceStatusBadge from '../InvoiceStatus/InvoiceStatusBadge';
import {
  InvoiceEquipment,
  InvoiceProduct,
  InvoiceService,
  InvoiceTag,
  InvoiceViewModel,
  InvoiceWorker,
} from '../../models/Invoice/InvoiceViewModel';
import InfoPerson from '../Person/InfoPerson';
import InvoiceServices from './InvoiceServices';
import InvoiceProducts from './InvoiceProducts';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import InvoiceEquipments from './InvoiceEquipments';
import InvoiceActionButtons from './InvoiceActionButtons';
import InvoiceComments, { InvoiceCommentType } from './InvoiceComments';
import InvoiceWorkers from './InvoiceWorkers';
import InvoicePayment from './InvoicePayment';
import InvoiceStatusState from '../../enums/InvoiceStatusState';
import { toMask } from '../../services/currency';
import Card from '../Card/Card';
import InvoiceInfo from './InvoiceInfo';
import InvoiceAttachments from './InvoiceAttachments';

import styles from './InvoiceFoo.module.scss';
import CustomButton from '../CustomButton/CustomButton';
import InvoiceTags from './InvoiceTags';
import CustomConfirm from '../CustomConfirm/CustomConfirm';

interface InvoiceFooProps {
  isLoading: boolean;
  invoice: InvoiceViewModel;
  refreshInvoice: () => void;
  // formFields: InvoiceFormFields;
  // formErrors: Record<string, any>;
  onSubmit: (data: any) => void;
}

export default function InvoiceFoo({
  isLoading,
  invoice,
  refreshInvoice,
  onSubmit,
}: InvoiceFooProps) {
  const {
    user: { organization },
    isAdmin,
  } = useAuth();

  const { register, getValues, reset } = useForm();
  const { search } = useLocation();

  const [invoiceProducts, setInvoiceProducts] = useState<InvoiceProduct[]>(
    invoice.invoiceProducts
  );
  const [invoiceServices, setInvoiceServices] = useState<InvoiceService[]>(
    invoice.invoiceServices
  );
  const [invoiceEquipments, setInvoiceEquipments] = useState<
    InvoiceEquipment[]
  >(invoice.invoiceEquipments);
  const [invoiceWorkers, setInvoiceWorkers] = useState<InvoiceWorker[]>(
    invoice.invoiceWorkers
  );
  const [invoiceTags, setInvoiceTags] = useState<InvoiceTag[]>(
    invoice.invoiceTags
  );

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isCustomConfirmOpenModal, setIsCustomConfirmOpenModal] =
    useState(false);
  const [isHideDiscount, setIsHideDiscount] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);

  useEffect(() => {
    setInvoiceProducts(invoice.invoiceProducts);
    setInvoiceServices(invoice.invoiceServices);
    setInvoiceEquipments(invoice.invoiceEquipments);
    setInvoiceWorkers(invoice.invoiceWorkers);
    setInvoiceTags(invoice.invoiceTags);
    reset({
      discount: invoice.discount,
    });
  }, [invoice]);

  const removeProduct = useCallback(
    async (invoiceProduct: InvoiceProduct) => {
      try {
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o produto "${invoiceProduct.name}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/invoices/${invoice.id}/products/${invoiceProduct.productId}`
          );
          refreshInvoice();
          toast.success(
            `O produto "${invoiceProduct.name}" foi removido da Ordem de Serviço`
          );
        }
      } catch (e) {
        // TODO: Recarregar lista
        toast.error(
          'Houve um problema ao tentar remover o produto da Ordem de Serviço. Tente novamente.'
        );
      }
    },
    [organization.id, invoice.id]
  );

  const removeService = useCallback(
    async (invoiceService: InvoiceService) => {
      try {
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o serviço "${invoiceService.name}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/invoices/${invoice.id}/services/${invoiceService.serviceId}`
          );
          refreshInvoice();
          toast.success(
            `O serviço "${invoiceService.name}" foi removido da Ordem de Serviço`
          );
        }
      } catch (e) {
        // TODO: Recarregar lista
        toast.error(
          'Houve um problema ao tentar remover o serviço da Ordem de Serviço. Tente novamente.'
        );
      }
    },
    [organization.id, invoice.id, refreshInvoice, toast]
  );

  const removeEquipment = useCallback(
    async (invoiceEquipment: InvoiceEquipment) => {
      try {
        const displayName = `${invoiceEquipment.name} (N/S: ${invoiceEquipment.serialNumber})`;
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o equipamento "${displayName}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/invoices/${invoice.id}/equipments/${invoiceEquipment.equipmentId}`
          );
          refreshInvoice();
          toast.success(
            `O equipamento "${displayName}" foi removido da Ordem de Serviço`
          );
        }
      } catch (e) {
        // TODO: Recarregar lista
        toast.error(
          'Houve um problema ao tentar remover o equipamento da Ordem de Serviço. Tente novamente.'
        );
      }
    },
    [organization.id, invoice.id, refreshInvoice, toast]
  );

  const undoPayment = async () => {
    try {
      if (
        // eslint-disable-next-line no-restricted-globals
        confirm(
          `Você tem certeza que deseja desfazer o pagamento? Essa ação não poderá ser desfeita.`
        )
      ) {
        await api.post(
          `organizations/${organization.id}/invoices/${invoice.id}/undo-payment`
        );
        refreshInvoice();
        toast.success(`O pagamento foi da Ordem de Serviço foi desfeito`);
      }
    } catch (e) {
      // TODO: Recarregar lista
      toast.error(
        'Houve um problema ao tentar desfazer o pagamento da Ordem de Serviço. Tente novamente.'
      );
    }
  };

  const removeWorker = useCallback(
    async (invoiceWorker: InvoiceWorker) => {
      try {
        const displayName = `${invoiceWorker.name}`;
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o colaborador "${displayName}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/invoices/${invoice.id}/workers/${invoiceWorker.workerId}`
          );
          refreshInvoice();
          toast.success(
            `O colaborador "${displayName}" foi removido da Ordem de Serviço`
          );
        }
      } catch (e) {
        // TODO: Recarregar lista
        toast.error(
          'Houve um problema ao tentar remover o colaborador da Ordem de Serviço. Tente novamente.'
        );
      }
    },
    [organization.id, invoice.id, refreshInvoice, toast]
  );

  const generatePdf = async (showValues: boolean) => {
    const { data: url } = await api.post(
      `organizations/${organization.id}/invoices/${invoice.id}/pdf`,
      {
        showValues,
      }
    );
    // @ts-ignore
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    win?.focus(); // seguro
  };

  const copyInvoicePath = () => {
    navigator.clipboard.writeText(
      `${window.location.host}/invoices/${invoice.id}/sign`
    );
    toast.success('Link copiado');
  };

  async function setDiscount(discount: number) {
    try {
      await api.post(
        `organizations/${organization.id}/invoices/${invoice.id}/discount`,
        { discount }
      );
      toast.success('Desconto registrado com sucesso');
      refreshInvoice();
      setIsHideDiscount(true);
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  }

  const addDiscount = async () => {
    const { discount } = getValues();
    await setDiscount(discount);
  };

  const removeDiscount = async () => {
    await setDiscount(0);
  };

  return (
    <div>
      <div className='d-flex align-items-center justify-content-between'>
        <div>
          <h1>Nº {invoice.number ? invoice.number : '#'}</h1>
          {invoice.estimateId && (
            <h5>
              <Link
                to={`/estimates/${invoice.estimateId}/edit`}
                target='_blank'
              >
                Orçamento #{invoice.estimateNumber}
              </Link>
            </h5>
          )}
        </div>
        <div>
          <div className={`${styles.invoiceHeaderBadges}`}>
            {invoice.isPaid && (
              <span className='badge bg-success text-uppercase'>Pago</span>
            )}
            <InvoiceStatusBadge
              state={invoice.statusState}
              label={invoice.statusLabel}
            />
          </div>
        </div>
      </div>
      <div className='d-flex justify-content-between border-top text-muted'>
        <div className='d-flex flex-column'>
          <span>Dada de Criação: {invoice.createdAt.toLocaleString()}</span>
          <span>{invoice.transactionNatureName}</span>
        </div>
        <div className='d-flex flex-column'>
          {invoice.startedAt && (
            <span>Data de Início: {invoice.startedAt.toLocaleString()}</span>
          )}
          {invoice.finishedAt && (
            <span>
              Data de Conclusão: {invoice.finishedAt.toLocaleString()}
            </span>
          )}
          {invoice.paymentDate && (
            <div className='d-flex flex-column'>
              <span>
                Data de Pagamento: {invoice.paymentDate.toLocaleString()}
              </span>
              <span>{invoice.paymentDescription}</span>
            </div>
          )}
        </div>
      </div>

      <div className='row justify-content-evenly mt-5 gap-3'>
        <div className='col-md-5'>
          <InfoPerson
            title='Empresa'
            name={invoice.companyName}
            alias={invoice.companyAlias}
            identifier={invoice.companyIdentifier}
            identifierType={invoice.companyType}
            editAction={`/companies/${invoice.companyId}/edit`}
          />
        </div>
        <div className='col-md-5'>
          <InfoPerson
            title='Cliente'
            name={invoice.customerName}
            alias={invoice.customerAlias}
            identifier={invoice.customerIdentifier}
            identifierType={invoice.customerType}
            editAction={`/customers/${invoice.customerId}/edit`}
          />
        </div>
      </div>
      <div className='mt-5'>
        <InvoiceWorkers
          invoice={invoice}
          items={invoiceWorkers}
          refreshWorkers={refreshInvoice}
          removeWorker={removeWorker}
        />
      </div>

      <div className='mt-5'>
        <div className='mt-5'>
          <InvoiceEquipments
            invoice={invoice}
            items={invoiceEquipments}
            refreshEquipments={refreshInvoice}
            removeEquipment={removeEquipment}
          />
        </div>
        <div className='mt-5'>
          <InvoiceServices
            invoice={invoice}
            items={invoiceServices}
            refreshServices={refreshInvoice}
            removeService={removeService}
          />
        </div>
        <div className='mt-5'>
          <InvoiceProducts
            invoice={invoice}
            items={invoiceProducts}
            refreshProducts={refreshInvoice}
            removeProduct={removeProduct}
          />
        </div>
        <div className='mt-1'>
          <InvoiceComments
            invoice={invoice}
            type={InvoiceCommentType.Technical}
          />
        </div>
        <hr />
        {isAdmin && (
          <div className='row gap-2'>
            <div className='col-sm'>
              <InvoiceInfo invoice={invoice} />
            </div>
            <div className='col-sm'>
              <Card header='Total e desconto' isLoading={false}>
                <div className='d-flex flex-column mt-2 fs-4'>
                  <div>
                    <span className='fw-bold'>Subtotal</span>:{' '}
                    {toMask(invoice.subTotal)}
                  </div>
                  {isHideDiscount && (
                    <div className='d-flex align-items-center'>
                      <span className='fw-bold'>Desconto</span>:{' '}
                      {toMask(invoice.discount)}
                      <button
                        type='button'
                        className='btn p-0 fs-4 mb-1 ms-2'
                        onClick={() => setIsHideDiscount(false)}
                      >
                        <FaRegEdit />
                      </button>
                    </div>
                  )}
                  {!isHideDiscount && (
                    <div className='d-flex align-items-center gap-2'>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        htmlFor='inputLabel'
                        className='form-label fw-bold'
                      >
                        Desconto:
                      </label>
                      <div
                        className='input-group h-75 input-group-sm'
                        style={{ width: '7rem' }}
                      >
                        <span className='input-group-text'>R$</span>
                        <input
                          {...register('discount')}
                          className='form-control'
                          placeholder='Desconto'
                          type='number'
                          min='.01'
                          step='.01'
                          required
                        />
                      </div>
                      <div className='d-flex align-items-center align-self-end gap-2 mb-2'>
                        <button
                          type='button'
                          className='btn btn-sm btn-success'
                          onClick={() => addDiscount()}
                        >
                          <FaCheckCircle />
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-danger'
                          onClick={() => removeDiscount()}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <span className='fw-bold'>Total</span>:{' '}
                    {toMask(invoice.total)}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
      <div className='mt-3'>
        <InvoiceAttachments invoiceId={invoice.id} />
      </div>
      <div className='mt-3'>
        {isAdmin && (
          <InvoiceTags
            invoiceId={invoice.id}
            items={invoice.invoiceTags}
            refreshTags={refreshInvoice}
          />
        )}
      </div>
      <div className='mt-1'>
        <InvoiceComments invoice={invoice} type={InvoiceCommentType.General} />
      </div>
      {isAdmin && (
        <>
          <div className='mt-1'>
            <InvoiceComments
              invoice={invoice}
              type={InvoiceCommentType.Internal}
            />
          </div>
          <div className='mt-1'>
            <InvoiceComments
              invoice={invoice}
              type={InvoiceCommentType.System}
            />
          </div>
        </>
      )}

      <hr />

      <div className='mt-1 mb-5 p-2 d-flex justify-content-between align-items-center flex-wrap gap-2'>
        <div className='btn-left d-flex justify-content-end gap-2'>
          {!invoice.isSigned && (
            <Link
              className='btn btn-secondary btn-primary'
              aria-current='page'
              to={`/invoices/${invoice.id}/sign?redirectUrl=/invoices/${invoice.id}/edit`}
            >
              Assinar <FaSignature />
            </Link>
          )}
          {!invoice.isSigned && (
            <button
              className='btn btn-secondary'
              type='button'
              onClick={copyInvoicePath}
            >
              Link de Assinatura <FaCopy />
            </button>
          )}
          {invoice.statusState !== InvoiceStatusState.Draft &&
            invoice.statusState !== InvoiceStatusState.Todo &&
            invoice.statusState !== InvoiceStatusState.Canceled && (
              <>
                <CustomButton
                  className='btn btn-danger'
                  type='button'
                  handleClick={async () => setIsCustomConfirmOpenModal(true)}
                >
                  PDF <FaFilePdf />
                </CustomButton>
                <CustomConfirm
                  isOpen={isCustomConfirmOpenModal}
                  setIsOpenModal={setIsCustomConfirmOpenModal}
                  onConfirm={() => generatePdf(true)}
                  onCancel={() => generatePdf(false)}
                  cancelLabel='Não'
                  confirmLabel='Sim'
                  message='Deseja exibir os valores da Ordem de Serviço no PDF?'
                />
              </>
            )}
          {isAdmin &&
            !invoice.isPaid &&
            invoice.statusState !== InvoiceStatusState.Draft &&
            invoice.statusState !== InvoiceStatusState.Canceled && (
              <button
                type='button'
                className='btn btn-success'
                onClick={() => setIsOpenModal(true)}
              >
                Pagar <MdPaid />
              </button>
            )}
          {isAdmin && invoice.isPaid && (
            <CustomButton
              type='button'
              className='btn btn-secondary'
              handleClick={undoPayment}
            >
              Desfazer pagamento <MdPaid />
            </CustomButton>
          )}
        </div>
        <div className='btn-right'>
          <InvoiceActionButtons
            invoice={invoice}
            refreshInvoice={refreshInvoice}
          />
        </div>
      </div>
      <div>
        <InvoicePayment
          invoiceId={invoice.id}
          isOpen={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          refreshInvoice={refreshInvoice}
        />
      </div>

      {invoice.isSigned && (
        <div>
          <img width={150} src={invoice.signature} alt='Assinatura' />
        </div>
      )}
    </div>
  );
}
